/* eslint-disable camelcase */
import { CustomFieldDefinition } from '@nx-smartmonkey/shared/domain';
import { dateFormat, secondsToMinutes } from '@nx-smartmonkey/shared/helpers';
import * as XLSX from 'xlsx';

import { CUSTOM_FIELD_COLUMN_ID_PREFIX } from '../../constants';
import { ClientsTableColumn, ClientsTableColumnKeys } from '../../context/client/tableColumns';
import { Client } from '../../domain/client/Client';
import { Project } from '../../domain/project/Project';
import { intl } from '../IntGlobalProvider';
import { ExcelEntryFormatter, customFieldToFormatter, jsonToExcel } from './Formatters';
import { parseStringArray, parseTimewindowsToExcel } from './Parsers';

const getClientFormatters = (
  parsedTableColumns: Record<ClientsTableColumnKeys, boolean>,
  project: Project
): ExcelEntryFormatter[] => {
  const volumeIn = project.units!.volume ?? ``;
  const weightIn = project.units!.weight ?? ``;
  const formatters: ExcelEntryFormatter[] = [];

  if (parsedTableColumns.label) {
    formatters.push({
      header: intl.formatMessage({ id: `clients.excel.columns.name` }),
      id: `label`,
      formatter: (client: Client) => client.label,
    });
  }

  if (parsedTableColumns.external_id) {
    formatters.push({
      header: intl.formatMessage({ id: `clients.excel.columns.external_id` }),
      id: `external_id`,
      formatter: (client: Client) => client.external_id,
    });
  }

  if (parsedTableColumns.location) {
    formatters.push({
      id: `location`,
      header: intl.formatMessage({ id: `clients.excel.columns.location` }),
      formatter: (client: Client) => (client.location ? `${client.location?.lat}, ${client.location?.lng}` : ``),
    });

    formatters.push({
      id: `address`,
      header: intl.formatMessage({ id: `clients.excel.columns.address` }),
      formatter: (client: Client) => client.location?.label ?? ``,
    });
  }

  if (parsedTableColumns.location_details) {
    formatters.push({
      id: `location_details`,
      header: intl.formatMessage({ id: `clients.excel.columns.address_2` }),
      formatter: (client: Client) => client.location_details,
    });
  }

  if (parsedTableColumns.comments) {
    formatters.push({
      id: `comments`,
      header: intl.formatMessage({ id: `clients.excel.columns.comments` }),
      formatter: (client: Client) => client.comments,
    });
  }

  if (parsedTableColumns.phone) {
    formatters.push({
      id: `phone`,
      header: intl.formatMessage({ id: `clients.excel.columns.phone` }),
      formatter: (client: Client) => client.phone,
    });
  }

  if (parsedTableColumns.email) {
    formatters.push({
      id: `email`,
      header: intl.formatMessage({ id: `clients.excel.columns.email` }),
      formatter: (client: Client) => client.email,
    });
  }

  if (parsedTableColumns.url) {
    formatters.push({
      id: `url`,
      header: intl.formatMessage({ id: `clients.excel.columns.url` }),
      formatter: (client: Client) => client.url,
    });
  }

  if (parsedTableColumns.reference_person) {
    formatters.push({
      id: `reference_person`,
      header: intl.formatMessage({ id: `clients.excel.columns.reference_person` }),
      formatter: (client: Client) => client.reference_person,
    });
  }

  if (parsedTableColumns.default_duration) {
    formatters.push({
      id: `default_duration`,
      header: intl.formatMessage({ id: `clients.excel.columns.default_duration` }),
      formatter: (client: Client) => secondsToMinutes(client.default_duration),
      type: `n`,
    });
  }

  // if (parsedTableColumns.default_reward) {
  //   formatters.push({
  //     id: `default_reward`,
  //     header: intl.formatMessage({ id: `clients.excel.columns.default_reward` }),
  //     formatter: (client: Client) => client.default_reward,
  //     type: `n`,
  //   });
  // }

  if (parsedTableColumns.default_requires) {
    formatters.push({
      id: `default_requires`,
      header: intl.formatMessage({ id: `clients.excel.columns.default_requires` }),
      formatter: (client: Client) => parseStringArray(client.default_requires),
    });
  }

  if (parsedTableColumns.default_cluster) {
    formatters.push({
      id: `default_cluster`,
      header: intl.formatMessage({ id: `clients.excel.columns.default_cluster` }),
      formatter: (client: Client) => client.default_cluster,
    });
  }

  // if (parsedTableColumns.default_assign_to) {
  //   formatters.push({
  //     id: `default_assign_to`,
  //     header: intl.formatMessage({ id: `clients.excel.columns.default_assign_to` }),
  //     formatter: (client: Client) => client.default_assign_to,
  //   });
  // }

  if (parsedTableColumns.default_time_windows) {
    formatters.push({
      id: `default_time_windows`,
      header: intl.formatMessage({ id: `clients.excel.columns.default_time_windows` }),
      formatter: (client: Client) =>
        client.default_time_windows ? parseTimewindowsToExcel(client.default_time_windows) : ``,
    });
  }

  if (parsedTableColumns.default_weight) {
    formatters.push({
      id: `default_weight`,
      header: `${intl.formatMessage({ id: `clients.excel.columns.default_weight` })} (${weightIn})`,
      formatter: (client: Client) => client.default_weight,
      type: `n`,
    });
  }

  if (parsedTableColumns.default_volume) {
    formatters.push({
      id: `default_volume`,
      header: `${intl.formatMessage({ id: `clients.excel.columns.default_volume` })} (${volumeIn})`,
      formatter: (client: Client) => client.default_volume,
      type: `n`,
    });
  }

  if (parsedTableColumns.created_at) {
    formatters.push({
      id: `created_at`,
      header: intl.formatMessage({ id: `clients.excel.columns.created_at` }),
      formatter: (client: Client) => dateFormat(client.created_at),
      type: `d`,
    });
  }

  return formatters;
};

export const exportClients = ({
  clients,
  project,
  tableColumns,
}: {
  clients: Client[];
  project: Project;
  tableColumns?: Array<ClientsTableColumn>;
}) => {
  const customFields: CustomFieldDefinition[] = project.custom_fields?.client ?? [];

  const wb = XLSX.utils.book_new();
  wb.Props = {
    Title: `Clients`,
    Subject: `ProjectBase Clients exported`,
    Author: `Routal`,
    CreatedDate: new Date(),
  };

  const parsedTableColumns = (tableColumns ?? []).reduce(
    (amount, next) => ({
      ...amount,
      [next.identifier]: next.displayable,
    }),
    {} as Record<ClientsTableColumnKeys, boolean>
  );

  const clientCustomFieldFormatter = customFields
    .filter(
      (cf) =>
        cf.field_model === `client` &&
        project.planner.stopHasCustomField(cf.custom_id) &&
        (parsedTableColumns as any)[`${CUSTOM_FIELD_COLUMN_ID_PREFIX}${cf.custom_id}`] === true
    )
    .map((cf) => {
      const formatter = customFieldToFormatter(cf);
      return {
        ...formatter,
        formatter: (client: Client) => formatter.formatter && formatter.formatter(client.custom_fields),
      };
    });

  const allClientsFormatter = [...getClientFormatters(parsedTableColumns, project), ...clientCustomFieldFormatter];

  const clientsSheet = jsonToExcel(clients, allClientsFormatter);

  const clientsLabel = intl.formatMessage({ id: `clients.excel.title` });
  wb.SheetNames.push(clientsLabel);
  wb.Sheets[clientsLabel] = clientsSheet;

  return XLSX.write(wb, { bookType: `xlsx`, type: `binary`, cellDates: true });
};
